import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("verifying"); // verifying | ok | error
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  // React 18 StrictMode double-invokes effects in dev. Verification tokens are
  // one-shot, so the second call would fail with "already used" and flip the
  // UI to an error state even though the user IS verified. Guard the fetch.
  const started = useRef(false);

  useEffect(() => {
    if (!token) { setStatus("error"); setMessage("Missing verification token."); return; }
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setStatus("ok");
        setMessage("Your email has been verified. Redirecting you to sign in…");
        setTimeout(() => navigate("/login", { replace: true }), 1600);
      } catch (e) {
        setStatus("error");
        const d = e?.response?.data?.detail;
        setMessage(typeof d === "string" ? d : "This verification link is invalid or has expired.");
      }
    })();
  }, [token, navigate]);

  return (
    <div className="min-h-[60vh] grid place-items-center px-4 py-12" data-testid="verify-email-page">
      <div className="text-center max-w-md">
        {status === "verifying" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto mb-4 text-sky-700 dark:text-sky-300 animate-spin"/>
            <h1 className="font-heading text-xl font-semibold text-slate-900 dark:text-slate-100">Verifying your email…</h1>
          </>
        )}
        {status === "ok" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-emerald-600" data-testid="verify-email-ok"/>
            <h1 className="font-heading text-xl font-semibold text-slate-900 dark:text-slate-100">You're verified</h1>
            <p className="text-slate-600 dark:text-slate-300 mt-2">{message}</p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-12 h-12 mx-auto mb-4 text-red-600" data-testid="verify-email-error"/>
            <h1 className="font-heading text-xl font-semibold text-slate-900 dark:text-slate-100">Couldn't verify</h1>
            <p className="text-slate-600 dark:text-slate-300 mt-2">{message}</p>
            <Link to="/login" className="inline-block mt-6 text-sm text-sky-700 dark:text-sky-300 hover:underline">
              ← Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
